import React, { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Star, CheckCircle2, Heart, RotateCcw } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Rating = ({ value, max, onChange, testid }) => (
  <div className="flex flex-wrap gap-1.5" data-testid={testid}>
    {Array.from({ length: max }, (_, i) => i + 1).map((n) => (
      <button key={n} type="button" onClick={() => onChange(n)}
        className={`h-10 w-10 rounded-lg border text-sm font-semibold transition-colors ${Number(value) >= n ? "bg-amber-400 border-amber-400 text-white" : "bg-white border-neutral-200 text-neutral-500 hover:border-amber-300"}`}
        data-testid={`${testid}-${n}`}>{max > 5 ? n : <Star className={`h-5 w-5 mx-auto ${Number(value) >= n ? "fill-white" : ""}`} />}</button>
    ))}
  </div>
);

export default function PublicSurvey() {
  const { token } = useParams();
  const [params] = useSearchParams();
  const kioskKey = params.get("k");
  const [activeToken, setActiveToken] = useState(null);
  const [survey, setSurvey] = useState(null);
  const [answers, setAnswers] = useState({});
  const [done, setDone] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const boot = async () => {
      try {
        let tok = token;
        if (token === "new") {
          if (!kioskKey) { setError("This kiosk link is missing its key."); return; }
          const { data } = await axios.post(`${API}/public/survey/kiosk/${kioskKey}`);
          tok = data.token;
        }
        setActiveToken(tok);
        const { data } = await axios.get(`${API}/public/survey/${tok}`);
        setSurvey(data);
        if (data.status === "completed") setDone({ happy: false, google_review_url: "", already: true });
      } catch (e) { setError("This survey link is invalid or has expired."); }
    };
    boot();
  }, [token, kioskKey]);

  const set = (id, v) => setAnswers((a) => ({ ...a, [id]: v }));

  const submit = async () => {
    setError(""); setLoading(true);
    try {
      const { data } = await axios.post(`${API}/public/survey/${activeToken}/submit`, { answers });
      setDone(data);
    } catch (e) { setError(e.response?.data?.detail || "Something went wrong. Please try again."); }
    finally { setLoading(false); }
  };

  const startAnother = () => { window.location.href = `/survey/new?k=${kioskKey}`; };

  if (error && !survey) return <div className="min-h-screen flex items-center justify-center p-6 text-center text-muted-foreground" data-testid="survey-error">{error}</div>;
  if (!survey) return <div className="min-h-screen flex items-center justify-center">Loading…</div>;

  if (done) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6 bg-neutral-50">
        <div className="w-full max-w-md text-center space-y-5" data-testid="survey-thankyou">
          <div className="h-16 w-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto"><CheckCircle2 className="h-9 w-9 text-emerald-600" /></div>
          <h1 className="font-heading font-bold text-2xl">Thank you!</h1>
          {done.already ? (
            <p className="text-muted-foreground">You've already completed this survey. We appreciate your feedback.</p>
          ) : done.happy && done.google_review_url ? (
            <>
              <p className="text-muted-foreground">So glad you had a great experience! Would you share it with others?</p>
              <a href={done.google_review_url} target="_blank" rel="noreferrer" data-testid="survey-google-cta">
                <Button className="w-full h-12 rounded-xl text-base"><Star className="h-5 w-5 mr-2 fill-white" />Leave us a Google review</Button>
              </a>
            </>
          ) : (
            <p className="text-muted-foreground flex items-center justify-center gap-1.5"><Heart className="h-4 w-4 text-rose-500" />Your feedback helps us improve. We'll be in touch.</p>
          )}
          {kioskKey && (
            <Button variant="outline" onClick={startAnother} className="w-full h-11 rounded-xl mt-2" data-testid="survey-start-another">
              <RotateCcw className="h-4 w-4 mr-2" />Start another survey
            </Button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-50 py-10 px-4">
      <div className="w-full max-w-lg mx-auto space-y-6" data-testid="public-survey">
        <div className="text-center">
          {survey.business?.logo_url && <img src={survey.business.logo_url} alt="" className="h-12 mx-auto mb-3" />}
          <h1 className="font-heading font-bold text-2xl">{survey.business?.name || "How did we do?"}</h1>
          <p className="text-muted-foreground text-sm mt-1">{survey.client_name ? `Hi ${survey.client_name} — ` : ""}your feedback takes under a minute 💛</p>
        </div>
        <div className="bg-white rounded-2xl border p-6 space-y-6">
          {survey.questions.map((q) => (
            <div key={q.id} className="space-y-2">
              <label className="text-sm font-medium block">{q.label}{q.required && <span className="text-rose-500"> *</span>}</label>
              {q.type === "rating" && <Rating value={answers[q.id]} max={q.max || 5} onChange={(v) => set(q.id, v)} testid={`survey-q-${q.id}`} />}
              {q.type === "textarea" && <Textarea value={answers[q.id] || ""} onChange={(e) => set(q.id, e.target.value)} className="rounded-lg" rows={3} data-testid={`survey-q-${q.id}`} />}
              {q.type === "choice" && (
                <div className="flex flex-wrap gap-2" data-testid={`survey-q-${q.id}`}>
                  {(q.options || []).map((o) => (
                    <button key={o} type="button" onClick={() => set(q.id, o)}
                      className={`px-4 h-10 rounded-lg border text-sm ${answers[q.id] === o ? "bg-primary border-primary text-white" : "bg-white border-neutral-200 hover:border-primary/40"}`}>{o}</button>
                  ))}
                </div>
              )}
            </div>
          ))}
          {error && <div className="text-sm text-destructive" data-testid="survey-submit-error">{error}</div>}
          <Button onClick={submit} disabled={loading} className="w-full h-12 rounded-xl text-base" data-testid="survey-submit">{loading ? "Sending…" : "Submit feedback"}</Button>
        </div>
      </div>
    </div>
  );
}
